import { ParsedUrlQueryInput, stringify } from 'querystring'
import { PermitListTabs, PermitStatus } from '@core/entities/permit/PermitBase'
import {
  ChecklistListTabs,
  ChecklistStatus,
} from '@core/entities/checklist/Checklist/types'
import { TaskTabs } from './entities/task/TaskBase'
import { CompaniesSettingsTabs } from '@core/entities/companySettings'
import { ProjectUsersTab } from '@core/entities/project/Project/types'
import { ObservationStatus } from '@core/entities/observation/types'
import { Priority } from '@core/entities/priority'
import { AdvancedDueDateFilterOperators } from './providers/filters'

const queryString = (query: ParsedUrlQueryInput | Record<string, any>) => {
  const modifiedQuery = Object.entries(query || {}).reduce(
    (acc, [key, value]) => {
      if (
        typeof value === 'object' &&
        !Array.isArray(value) &&
        value !== null
      ) {
        acc[key] = JSON.stringify(value)
      } else {
        acc[key] = value
      }
      return acc
    },
    {} as ParsedUrlQueryInput,
  )

  const stringified = stringify(modifiedQuery)
  if (stringified) {
    return `?${stringified}`
  } else {
    return ''
  }
}

export const PageUrls = {
  actions: (id: number | string) => `/organisations/${id}/actions`,
  adminAccessToken: `/support/access-token`,
  adminAnalytics: `/support/analytics`,
  adminUser: (id: number | string) => `/support/users/${id}`,
  adminOrganisations: `/support/organisations`,
  adminUsers: `/support/users`,
  adminPermits: `/support/permits`,
  addressRequestedPermitChanges: (args: {
    organisationId: number | string
    permitId: number | string
  }) => `/organisations/${args.organisationId}/permits/${args.permitId}/change`,
  addressRequestedChecklistChanges: (args: {
    organisationId: number | string
    checklistId: number | string
  }) =>
    `/organisations/${args.organisationId}/checklists/${args.checklistId}/change`,
  addressRequestedProjectChecklistChanges: (args: {
    organisationId: number
    projectId: number
    checklistId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/checklists/${args.checklistId}/change`,
  documentIntelligence: (args: {
    organisationId: number
    projectId: number
    documentId: number
    versionId: number
    view?: 'summary' | 'document_properties' | 'form' | 'query_result_appraisal'
    documentUrl?: string
  }) => {
    const path = `/organisations/${args.organisationId}/projects/${args.projectId}/site-box/${args.documentId}/version/${args.versionId}`
    const params = []
    if (args.view) params.push(`view=${args.view}`)
    if (args.documentUrl)
      params.push(`documentUrl=${encodeURIComponent(args.documentUrl)}`)
    return params.length ? `${path}?${params.join('&')}` : path
  },
  addressRequestedProjectPermitChanges: (args: {
    organisationId: number
    projectId: number
    permitId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/permits/${args.permitId}/change`,
  contacts: (id: number | string) => `/organisations/${id}/contacts`,
  createOrganisation: `/support/organisations/create`,
  editTemplate: (orgId: number | string, templateId) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}`,
  editChecklistTemplate: (orgId: number | string, templateId) =>
    `/organisations/${orgId}/settings/checklist-templates/${templateId}`,
  editInspectionTemplate: (orgId: number | string, templateId) =>
    `/organisations/${orgId}/settings/inspection-templates/${templateId}`,
  editCompetencyTemplate: (orgId: number | string, templateId: number) =>
    `/organisations/${orgId}/settings/competency-templates/${templateId}`,
  editCompetencyTemplateVersion: ({
    orgId,
    templateId,
    versionId,
  }: {
    orgId: number | string
    templateId: number
    versionId: number
  }) =>
    `/organisations/${orgId}/settings/competency-templates/${templateId}/versions/${versionId}`,
  editRegisterTemplate: ({
    orgId,
    templateId,
    step,
  }: {
    orgId: number | string
    templateId: number | string
    step?: string
  }) => {
    let url = `/organisations/${orgId}/settings/register-templates/${templateId}`
    if (step) {
      url += `?step=${step}`
    }
    return url
  },
  editTemplateVersion: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}`,
  editChecklistTemplateVersion: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/checklist-templates/${templateId}/versions/${versionId}`,
  editInspectionTemplateVersion: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/inspection-templates/${templateId}/versions/${versionId}`,
  createTemplatePermitRole: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}/permit-roles/create`,
  editTemplatePermitRole: ({ orgId, templateId, versionId, id }) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}/permit-roles/${id}`,
  createTemplatePeriodicCheck: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}/periodic-checks/create`,
  editTemplatePeriodicCheck: ({ orgId, templateId, versionId, id }) =>
    `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}/periodic-checks/${id}`,
  organisationViewTemplateVersion: ({
    orgId,
    templateId,
    versionId,
    stage,
  }: {
    orgId: number | string
    templateId: number | string
    versionId: number | string
    stage?: 'authorisation' | 'settings'
  }) => {
    let url = `/organisations/${orgId}/settings/permit-templates/${templateId}/versions/${versionId}`
    if (stage) {
      url += `?tab=${stage}`
    }
    return url
  },
  projectViewTemplateVersion: ({
    orgId,
    templateId,
    versionId,
    projectId,
    stage,
  }: {
    orgId: number | string
    projectId: number | string
    templateId: number | string
    versionId: number | string
    stage?: 'authorisation' | 'settings'
  }) => {
    let url = `/organisations/${orgId}/projects/${projectId}/settings/permit-templates/${templateId}/versions/${versionId}`
    if (stage) {
      url += `?stage=${stage}`
    }
    return url
  },
  projectViewChecklistTemplateVersion: ({
    orgId,
    templateId,
    versionId,
    projectId,
  }: {
    orgId: number | string
    projectId: number | string
    templateId: number | string
    versionId: number | string
  }) => {
    return `/organisations/${orgId}/projects/${projectId}/settings/checklist-templates/${templateId}/versions/${versionId}`
  },
  projectViewInspectionTemplateVersion: ({
    orgId,
    templateId,
    versionId,
    projectId,
  }: {
    orgId: number | string
    projectId: number | string
    templateId: number | string
    versionId: number | string
    tab?: 'authorisation' | 'settings'
  }) => {
    return `/organisations/${orgId}/projects/${projectId}/settings/inspection-templates/${templateId}/versions/${versionId}`
  },
  home: `/`,
  inspections: (
    id: number | string,
    query?: {
      projectIds?: number | string
      statuses?: string[]
      sort?: string
      tab?: ChecklistListTabs
      userId?: number
    },
    suffix = '',
  ) => {
    return `/organisations/${id}/inspections${suffix}${queryString(query)}`
  },
  inspection: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/inspections/${id}`,
  organisation: (id: number | string) => `/organisations/${id}`,
  organisationInviteUrl: `/organisations/accept-invite`,
  permits: (
    orgId: number | string,
    query?: {
      projectIds?: number[]
      statuses?: string[]
      templateIds?: number[]
      processTagIds?: number[]
      sort?: string
      tab?: PermitListTabs
      hideTestProjects?: boolean
      userId?: number
      registerItemId?: number
    },
    suffix = '',
  ) => {
    return `/organisations/${orgId}/permits${suffix}${queryString(query)}`
  },
  checklists: (
    orgId: number | string,
    query?: {
      projectIds?: number | string
      statuses?: string[]
      sort?: string
      tab?: ChecklistListTabs
      userId?: number
      isUsedForInspections?: boolean
    },
    suffix = '',
  ) => {
    return `/organisations/${orgId}/checklists${suffix}${queryString(query)}`
  },
  registers: (
    orgId: number | string,
    query?: {
      term?: string
      status?: string[]
      project?: string[]
      tab?
    },
    suffix = '',
  ) => {
    return `/organisations/${orgId}/registers${suffix}${queryString(query)}`
  },
  tasks: (
    orgId: number | string,
    query?: {
      tab?: TaskTabs
    },
  ) => `/organisations/${orgId}/tasks${queryString(query)}`,
  task: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/tasks/${id}`,
  projectTaskDetail: (args: {
    organisationId: number | string
    projectId: number | string
    taskId: number | string
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/tasks/${args.taskId}`,
  observation: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/observations/${id}`,
  projectObservationDetail: (args: {
    organisationId: number | string
    projectId: number | string
    observationId: number | string
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/observations/${args.observationId}`,
  projectObservations: (args: {
    organisationId: number | string
    projectId: number | string
    query?: {
      status?: ObservationStatus | 'ALL'
      priorities?: Priority[]
      dateRange?: {
        startTime: string
        endTime: string
      }
    }
  }) =>
    `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/observations${queryString(args.query)}`,
  projectObservation: (args: {
    organisationId: number | string
    projectId: number | string
    observationId: number | string
    status?: string
  }) =>
    `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/observations${queryString({
      observationId: args.observationId,
      status: args.status,
    })}`,
  newTask: (orgId: number | string) => `/organisations/${orgId}/tasks/new`,
  newTaskProject: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/tasks/new`,
  newPermit: (orgId: number | string, query?: ParsedUrlQueryInput) =>
    `/organisations/${orgId}/permits/new${queryString(query)}`,
  newPermitProject: (args: {
    organisationId: number
    projectId: number
    query?: ParsedUrlQueryInput
  }) =>
    `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/permits/new${queryString(args.query)}`,
  permitApproverQuestions: (orgId: number | string, id: number | string) => {
    return `/organisations/${orgId}/permits/${id}/approve`
  },
  projectPermitApproverQuestions: (
    orgId: number | string,
    projectId: number | string,
    id: number | string,
  ) => {
    return `/organisations/${orgId}/projects/${projectId}/permits/${id}/approve`
  },
  checklistApproverQuestions: (orgId: number | string, id: number | string) => {
    return `/organisations/${orgId}/checklists/${id}/approve`
  },
  projectChecklistApproverQuestions: (
    orgId: number | string,
    projectId: number | string,
    id: number | string,
  ) => {
    return `/organisations/${orgId}/projects/${projectId}/checklists/${id}/approve`
  },
  permitFinalSignOffs: (orgId: number | string, id: number | string) => {
    return `/organisations/${orgId}/permits/${id}/final-signoffs`
  },
  projectPermitFinalSignOffs: (
    orgId: number | string,
    projectId: number | string,
    id: number | string,
  ) => {
    return `/organisations/${orgId}/projects/${projectId}/permits/${id}/final-signoffs`
  },
  permitSignoffCheck: (args: { organisationId: number; permitId: number }) => {
    return `/organisations/${args.organisationId}/permits/${args.permitId}/signoff`
  },
  projectPermitSignoffCheck: (args: {
    organisationId: number
    projectId: number
    permitId: number
  }) => {
    return `/organisations/${args.organisationId}/projects/${args.projectId}/permits/${args.permitId}/signoff`
  },
  newChecklist: (organisationId: number | string) =>
    `/organisations/${organisationId}/checklists/new`,
  newChecklistProject: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/checklists/new`,
  newInspection: (organisationId: number | string) =>
    `/organisations/${organisationId}/checklists/new?isUsedForInspections=true`,
  newInspectionProject: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/checklists/new?isUsedForInspections=true`,
  permit: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/permits/${id}`,
  checklist: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/checklists/${id}`,
  permitDraft: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/permits/draft/${id}`,
  checklistDraft: (
    orgId: number | string,
    id: number | string,
    query?: { isUsedForInspections?: boolean },
  ) => `/organisations/${orgId}/checklists/draft/${id}${queryString(query)}`,
  taskDraft: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/tasks/draft/${id}`,
  receivePermit: (organisationId: number, permitId: number) =>
    `/organisations/${organisationId}/permits/${permitId}/receive`,
  receiveProjectPermit: (
    organisationId: number,
    projectId: number,
    permitId: number,
  ) =>
    `/organisations/${organisationId}/projects/${projectId}/permits/${permitId}/receive`,
  project: (orgId: number | string, id: number | string) =>
    `/organisations/${orgId}/projects/${id}`,
  projectOverview: (params: {
    organisationId: number | string
    projectId: number | string
    query?: {
      fullScreen?: boolean
      tab?: 'Overview'
    }
  }) => {
    return `/organisations/${params.organisationId}/projects/${
      params.projectId
    }/overview${queryString(params.query)}`
  },
  projectKiosk: (args: { organisationId: number; projectId: number }) => {
    return `/organisations/${args.organisationId}/projects/${args.projectId}/kiosk`
  },
  projectMap: (args: { organisationId: number; projectId: number }) => {
    return `/organisations/${args.organisationId}/projects/${args.projectId}/map`
  },
  projectPermits: (
    orgId: number | string,
    id: number | string,
    query?: {
      statuses?: PermitStatus[]
      sort?: string
      templateIds?: number[] | string[]
      locationIds?: number[] | string[]
      processTagIds?: number[] | string[]
      userId?: number
      tab?: PermitListTabs
      startDate?: {
        startTime: string
        endTime: string
      }
    },
  ) => {
    return `/organisations/${orgId}/projects/${id}/permits${queryString(query)}`
  },
  projectPermitDetail: (params: {
    organisationId: number | string
    projectId: number | string
    permitId: number | string
  }) =>
    `/organisations/${params.organisationId}/projects/${params.projectId}/permits/${params.permitId}`,
  projectChecklists: (args: {
    organisationId: number
    projectId: number
    query?: {
      projectIds?: number | string
      statuses?: string[]
      sort?: string
      userId?: number
      tab?: ChecklistListTabs
      isUsedForInspections?: boolean
      dateRange?: {
        startTime: string
        endTime: string
      }
    }
  }) => {
    return `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/checklists${queryString(args.query)}`
  },
  projectInspections: (args: {
    organisationId: number
    projectId: number
    query?: {
      projectIds?: number | string
      statuses?: string[]
      sort?: string
      userId?: number
      tab?: ChecklistListTabs
      dateRange?: {
        startTime: string
        endTime: string
      }
    }
  }) => {
    return `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/inspections${queryString(args.query)}`
  },
  projectChecklistDetail: (params: {
    organisationId: number | string
    projectId: number | string
    checklistId: number | string
  }) =>
    `/organisations/${params.organisationId}/projects/${params.projectId}/checklists/${params.checklistId}`,
  projectInspection: (params: {
    organisationId: number | string
    projectId: number | string
    checklistId: number | string
  }) =>
    `/organisations/${params.organisationId}/projects/${params.projectId}/inspections/${params.checklistId}`,
  organisationTimeline: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/timeline`,
  projectTimeline: (orgId: number | string, id) =>
    `/organisations/${orgId}/projects/${id}/timeline`,
  projectTasks: (args: {
    organisationId: number | string
    projectId: number | string
    query?: {
      tab?: TaskTabs
      isUsedForInspection?: {
        operator: 'IS_USED_FOR_INSPECTION'
      }
      isObservation?: boolean
      dueDate?: {
        operator: AdvancedDueDateFilterOperators
      }
      dateRange?: {
        startTime: string
        endTime: string
      }
    }
  }) =>
    `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/tasks${queryString(args.query)}`,
  projectUsers: (
    orgId: number | string,
    id: number | string,
    query?: { tab?: ProjectUsersTab },
  ) => `/organisations/${orgId}/projects/${id}/users${queryString(query)}`,
  organisationUsers: (orgId) => `/organisations/${orgId}/users`,
  organisationAuthorisationGroupList: (args: {
    organisationId: number | string
  }) => `/organisations/${args.organisationId}/settings/groups`,
  projectAuthorisationGroupList: (args: {
    organisationId: number | string
    projectId: number | string
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/groups`,
  projectSettings: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings`,
  projectSettingsGeneral: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/general`,
  projectIntegrations: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/integrations`,
  projectFeatureFlagsSettings: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/feature-flags`,
  organisationAuthorisationGroup: (args: {
    organisationId: number | string
    groupId: number | string
  }) => `/organisations/${args.organisationId}/settings/groups/${args.groupId}`,
  projectSiteBox: (args: {
    organisationId: number
    projectId: number
    query?: {
      folderId?: number
    }
  }) => {
    return `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/site-box${queryString(args.query)}`
  },
  projectProcore: (args: {
    organisationId: number
    projectId: number
    query?: {
      folderId?: number
    }
  }) => {
    return `/organisations/${args.organisationId}/projects/${
      args.projectId
    }/procore${queryString(args.query)}`
  },
  projectSiteBoxDocument: (args: {
    organisationId: number
    projectId: number
    folderId?: number
    documentId: number
    locationArchived?: boolean
  }) => {
    const query = queryString({
      folderId: args.folderId,
      documentId: args.documentId,
      locationArchived: args.locationArchived,
    })
    return `/organisations/${args.organisationId}/projects/${args.projectId}/site-box/document${query}`
  },
  projectAuthorisationGroup: (args: {
    organisationId: number | string
    projectId: number | string
    groupId: number | string
    tab?: 'Members' | 'Permissions' | 'Timeline'
  }) => {
    let url = `/organisations/${args.organisationId}/projects/${args.projectId}/settings/groups/${args.groupId}`
    if (args?.tab) {
      url += `?tab=${args.tab}`
    }
    return url
  },
  projectLocations: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/locations`,
  projectLocationPlan: (args: {
    organisationId: number
    projectId: number
    nodeId?: string
    planId?: number
    nodePath: string
    locationArchived: boolean
  }) => {
    const query = queryString({
      nodeId: args?.nodeId,
      planId: args?.planId,
      nodePath: args.nodePath,
      locationArchived: args.locationArchived,
    })
    return `/organisations/${args.organisationId}/projects/${args.projectId}/settings/locations/plan${query}`
  },
  projectMapSettings: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/map`,
  projectObservationSettings: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/observations`,
  projectViewPlans: (orgId: number | string, id) =>
    `/organisations/${orgId}/projects/${id}/view-plans`,
  projectRegisters: (orgId: number | string, id, query?: ParsedUrlQueryInput) =>
    `/organisations/${orgId}/projects/${id}/registers${queryString(query)}`,
  registerDetails: (args: {
    organisationId: number | string
    registerId: number
    query?: ParsedUrlQueryInput
  }) => {
    const urlQuery = queryString(args?.query ?? {})
    return `/organisations/${args.organisationId}/registers/${args.registerId}${urlQuery}`
  },
  organisationRegisterDetailsDiagram: (args: {
    organisationId: number | string
    registerId: number
    query?: ParsedUrlQueryInput
  }) => {
    const urlQuery = queryString(args?.query ?? {})
    return `/organisations/${args.organisationId}/registers/${args.registerId}/diagrams${urlQuery}`
  },
  projectRegisterDetailsDiagram: (args: {
    organisationId: number | string
    projectId: number | string
    registerId: number
    query?: ParsedUrlQueryInput
  }) => {
    const urlQuery = queryString(args?.query ?? {})
    return `/organisations/${args.organisationId}/projects/${args.projectId}/registers/${args.registerId}/diagrams${urlQuery}`
  },
  registerSettings: (orgId: number | string, registerId: number) =>
    `/organisations/${orgId}/registers/${registerId}/settings`,
  projectRegisterDetails: (args: {
    organisationId: number | string
    projectId: number | string
    registerId: number
    query?: ParsedUrlQueryInput
  }) => {
    const urlQuery = queryString(args?.query ?? {})
    return `/organisations/${args.organisationId}/projects/${args.projectId}/registers/${args.registerId}${urlQuery}`
  },
  projectRegisterSettings: (orgId: number | string, id, registerId: number) =>
    `/organisations/${orgId}/projects/${id}/registers/${registerId}/settings`,
  registerItemDetails: (args: {
    organisationId: number
    registerId: number
    registerItemId: number
    registerViewId?: number
    tab?: string
  }) => {
    const query = queryString({
      viewId: args?.registerViewId,
      tab: args?.tab,
    })
    return `/organisations/${args.organisationId}/registers/${args.registerId}/items/${args.registerItemId}${query}`
  },
  projectRegisterItemDetails: (args: {
    organisationId: number
    projectId: number
    registerId: number
    registerItemId: number
    viewId?: number
  }) => {
    const query = queryString({
      viewId: args.viewId,
    })
    return `/organisations/${args.organisationId}/projects/${args.projectId}/registers/${args.registerId}/items/${args.registerItemId}${query}`
  },
  projectPermitTemplates: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/permit-templates`,
  projectChecklistTemplates: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/checklist-templates`,
  projectInspectionTemplates: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/inspection-templates`,
  projectDocumentEngineTemplates: (args: {
    organisationId: number
    projectId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/document-templates`,
  projectTaskTemplates: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/task-templates`,
  projectDigests: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/digests`,
  projectViewTaskTemplateVersion: (args: {
    organisationId: number
    projectId: number
    taskTemplateId: number
    taskTemplateVersionId: number
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/settings/task-templates/${args.taskTemplateId}/versions/${args.taskTemplateVersionId}`,
  projectTemplateDetail: (
    orgId: number | string,
    projectId: number | string,
    templateId: number | string,
  ) =>
    `/organisations/${orgId}/projects/${projectId}/settings/permit-templates/${templateId}`,
  projects: (id: number | string) => `/organisations/${id}/projects`,
  businessUnits: (id: number | string) => `/organisations/${id}/business-units`,
  businessUnitOverview: ({
    id,
    businessUnitId,
  }: {
    id: string | number
    businessUnitId: string | number
  }) => `/organisations/${id}/business-units/${businessUnitId}/overview`,
  businessUnitProjects: ({
    id,
    businessUnitId,
  }: {
    id: string | number
    businessUnitId: string | number
  }) => `/organisations/${id}/business-units/${businessUnitId}/projects`,
  businessUnitEdit: ({
    id,
    businessUnitId,
  }: {
    id: string | number
    businessUnitId: string | number
  }) => `/organisations/${id}/business-units/${businessUnitId}/edit`,
  businessUnitPermits: ({
    id,
    businessUnitId,
    query,
  }: {
    id: number | string
    businessUnitId: number | string
    query?: { statuses?: PermitStatus[]; sort?: string }
  }) => {
    return `/organisations/${id}/business-units/${businessUnitId}/permits${queryString(
      query,
    )}`
  },
  businessUnitChecklists: ({
    id,
    businessUnitId,
    query,
  }: {
    id: number | string
    businessUnitId: number | string
    query?: { statuses?: ChecklistStatus[]; sort?: string }
  }) => {
    return `/organisations/${id}/business-units/${businessUnitId}/checklists${queryString(
      query,
    )}`
  },
  organisationSettings: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings`,
  organisationCompanies: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/companies`,
  organisationCompanyDetail: (args: {
    organisationId: number
    companyId: number
  }) => `/organisations/${args.organisationId}/companies/${args.companyId}`,
  reviewCompany: (args: { organisationId: number; companyId: number }) =>
    `/organisations/${args.organisationId}/companies/${args.companyId}/review`,
  companyOnboarding: (args: { organisationId: number | string }) =>
    `/organisations/${args.organisationId}/companies/onboarding`,
  organisationCompaniesSettings: (args: {
    organisationId: number
    tab?: CompaniesSettingsTabs
  }) => {
    const url = `/organisations/${args.organisationId}/settings/companies`
    return args?.tab ? `${url}?tab=${args.tab}` : url
  },
  organisationGeneralSettings: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/general`,
  organisationAuthenticationSettings: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/authentication`,
  organisationSSOSettings: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/sso`,
  organisationFeatureFlagsSettings: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/feature-flags`,
  organisationPermitTemplates: (args: {
    organisationId: number
    query?: {
      folderId?: number
    }
  }) =>
    `/organisations/${
      args.organisationId
    }/settings/permit-templates${queryString(args.query)}`,
  organisationChecklistTemplates: (args: {
    organisationId: number
    query?: {
      folderId?: number
    }
  }) =>
    `/organisations/${
      args.organisationId
    }/settings/checklist-templates${queryString(args.query)}`,
  organisationInspectionTemplates: (args: {
    organisationId: number
    query?: {
      folderId?: number
    }
  }) =>
    `/organisations/${
      args.organisationId
    }/settings/inspection-templates${queryString(args.query)}`,
  organisationCompetencyTemplates: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/competency-templates`,
  competencyInduction: (args: {
    organisationId: number
    assignmentId: number
  }) =>
    `/organisations/${args.organisationId}/competencies/induction/${args.assignmentId}`,
  organisationRegisterTemplates: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/register-templates`,
  organisationDocumentEngineTemplates: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/document-engine-templates`,
  editDocumentEngineTemplate: (args: {
    organisationId: number
    templateId: number
  }) =>
    `/organisations/${args.organisationId}/settings/document-engine-templates/${args.templateId}`,
  editDocumentEngineTemplateVersion: (args: {
    organisationId: number
    templateId: number
    versionId: number
  }) =>
    `/organisations/${args.organisationId}/settings/document-engine-templates/${args.templateId}/versions/${args.versionId}`,
  organisationProcessTags: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/tags`,
  organisationTaskTemplates: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/task-templates`,
  editTaskTemplate: (orgId: number | string, templateId) =>
    `/organisations/${orgId}/settings/task-templates/${templateId}`,
  editTaskTemplateVersion: ({ orgId, templateId, versionId }) =>
    `/organisations/${orgId}/settings/task-templates/${templateId}/versions/${versionId}`,
  editTaskTemplateVersionStep: ({
    orgId,
    templateId,
    versionId,
    stage,
  }: {
    orgId: number | string
    templateId: number | string
    versionId: number | string
    stage?: string
  }) => {
    let url = `/organisations/${orgId}/settings/task-templates/${templateId}/versions/${versionId}`
    if (stage) {
      url += `?stage=${stage}`
    }
    return url
  },
  organisationAnalytics: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/analytics`,
  organisationConversationalAnalytics: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/analytics/conversational`,
  organisationAnalyticsDashboard: (args: {
    organisationId: number
    dashboardId: number | string
  }) =>
    `/organisations/${args.organisationId}/analytics/dashboards/${args.dashboardId}`,
  projectAnalytics: (args: { organisationId: number; projectId: number }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/analytics`,
  projectAnalyticsDashboard: (args: {
    organisationId: number
    projectId: number
    dashboardId: number | string
  }) =>
    `/organisations/${args.organisationId}/projects/${args.projectId}/analytics/dashboards/${args.dashboardId}`,
  businessUnitAnalytics: (args: {
    organisationId: number
    businessUnitId: number
  }) =>
    `/organisations/${args.organisationId}/business-units/${args.businessUnitId}/analytics`,
  businessUnitAnalyticsDashboard: (args: {
    organisationId: number
    businessUnitId: number
    dashboardId: number | string
  }) =>
    `/organisations/${args.organisationId}/business-units/${args.businessUnitId}/analytics/dashboards/${args.dashboardId}`,
  linkedOrganisations: (id: number | string) =>
    `/organisations/${id}/settings/links`,
  reports: (id: number | string) => `/organisations/${id}/reports`,
  mobileBriefing: (args: {
    checklistId: number
    accessToken: string
    organisationId: number
    projectId: number
    validUntil: string
  }) => `/mobile/briefing${queryString(args)}`,
  mfa: (query?: { code?: string; sessionId?: string }) =>
    `/mfa${queryString(query)}`,
  mfaAutoVerification: (query?: { code?: string }) =>
    `/mfa/auto-mfa-verification${queryString(query)}`,
  login: '/login',
  forgotPassword: (query?: { email?: string }) =>
    `/forgot-password${queryString(query)}`,
  forgotPasswordEmailSent: '/forgot-password-email-sent',
  noOrganisation: '/no-organisation',
  resetPassword: '/reset-password',
  resetPasswordSuccess: '/reset-password-success',
  federatedLoginRedirect: () => {
    return `${document.location.origin}/oauth/web-redirect`
  },
  developerSection: (args: { organisationId: number }) =>
    `/organisations/${args.organisationId}/settings/developer`,
  organisationUserProfile: (args: {
    organisationId: number
    userId: number
    query?: { projectId?: number; tab?: string }
  }) =>
    `/organisations/${args.organisationId}/users/${args.userId}${queryString(
      args.query,
    )}`,
  pdfDemo: '/poc/pdf-demo',
  supportKafka: '/support/kafka',
  supportLabelling: '/support/prodigy-labelling',
  supportMapboxWsm: '/support/mapbox-wsm',
  supportKmzPoc: '/support/kmz-poc',
}
